<template>
  <div id="center-general-information" class="flex flex-col gap-6 p-6">
    <div class="border bg-pure-white rounded-3xl border-slate-400">
      <div
        class="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-400"
      >
        <div>
          <div class="mb-2">
            <BlTextTitle4 text="General Information" />
          </div>
          <div>
            <BlTextBody2
              text="Manage your clinic general settings and information."
            />
          </div>
        </div>
        <div class="flex items-center gap-4">
          <BlInputButton
            type="outlined"
            label="Cancel"
            @clicked="generateInputs(currentCenter)"
          />
          <BlInputButton
            label="Update"
            :loading="submitLoading"
            :disabled="isDisabledSubmit"
            @clicked="onUpdate"
          />
        </div>
      </div>
      <div class="px-6 py-6">
        <div v-if="pageLoading" class="grid w-full h-80 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div v-else class="grid items-center grid-cols-10 gap-4">
          <BlTextBody2 text="Logo" class="col-span-2 text-slate-900" />
          <div class="grid grid-cols-2 col-span-8">
            <input
              type="file"
              name="logo_url_input"
              id="logo_url_input"
              hidden
              @change="onButtonUpload"
            />
            <div
              v-if="!logoUrlInput"
              class="flex flex-col items-center justify-center gap-2 transition-all duration-300 border-2 border-dashed rounded-lg cursor-pointer h-25 border-slate-500 hover:border-solid hover:border-dark-purple-200"
              @click="openUpload"
              :data-active="dropActive"
              @dragenter.prevent="dropActive = true"
              @dragover.prevent="dropActive = true"
              @dragleave.prevent="dropActive = false"
              @drop.prevent="onDropUpload"
              :class="{ 'bg-slate-200': dropActive }"
            >
              <BlIcon icon="picture" class="w-6 h-6" />
              <BlTextBody2
                text="Drop a picture or click to upload a logo"
                class="transition-all duration-300 text-slate-600 hover:text-dark-purple-200"
              />
            </div>
            <div v-else class="flex items-center">
              <img :src="logoUrlInput" alt="logo_url" class="h-25" />
              <BlInputButton
                type="plain"
                label="Remove logo"
                @clicked="onRemoveLogo"
              />
            </div>
          </div>
          <BlTextBody2 text="Center name" class="col-span-2 text-slate-900" />
          <div class="grid grid-cols-2 col-span-8">
            <BlInputText :input="nameInput" @changed="inputHandler" />
          </div>
          <BlTextBody2
            text="Currency for Credits in Appointments"
            class="col-span-2 text-slate-900"
          />
          <div class="grid grid-cols-2 col-span-8">
            <div class="relative">
              <BlSelectBox
                search
                v-model="currencyInput"
                placeholder="Select currency"
                :options="currencyOptions"
                :disabled="currentCenter.currency_confirmed"
                @change="onChangeCurrency($event)"
              />
              <BlTooltip
                v-if="currentCenter.currency_confirmed"
                class="absolute top-0 right-0 z-10 grid w-10 h-10 place-items-center"
              >
                <template v-slot:tooltip>
                  <div class="w-40">
                    To change the currency, please contact the Blüm support
                    team.
                  </div>
                </template>
                <BlIcon icon="info" class="w-6 h-6" />
              </BlTooltip>
            </div>
          </div>

          <!--
          <BlTextBody2 text="Date format" class="col-span-2 text-slate-900" />
          <div class="grid grid-cols-2 col-span-8">
            <BlSelectBox
              v-model="dateFormatInput"
              placeholder="Select date format"
              :options="dateFormatOptions"
              @change="dateFormatInput = $event"
            />
          </div> -->
          <!--
          <BlTextBody2 text="Timezone" class="col-span-2 text-slate-900" />
          <div class="grid grid-cols-2 col-span-8">
            <BlSelectBox
              search
              v-model="timezoneInput"
              placeholder="Select timezone"
              :options="timezoneOptions"
              @change="timezoneInput = $event"
            />
          </div> -->
        </div>
      </div>
    </div>

    <!-- <div class="border bg-pure-white rounded-3xl border-slate-400">
      <div class="flex items-center justify-between p-6">
        <div>
          <div class="flex items-center gap-2 mb-2">
            <BlInputToggle
              :checked="currentCenter.enable_branch"
              @changed="onChangeBranch"
            />
            <BlTextTitle4 text="Branch" />
          </div>
          <div>
            <BlTextBody2
              text="Activate this setting to manage and tag clients by branch for centers with multiple locations."
            />
          </div>
        </div>
        <div class="flex items-center gap-4">
          <router-link
            to="/repository?tab=branch"
            :class="{ 'pointer-events-none': !currentCenter.enable_branch }"
          >
            <BlInputButton
              :disabled="!currentCenter.enable_branch"
              label="View tags"
            />
          </router-link>
        </div>
      </div>
    </div> -->

    <BlBackdrop
      v-if="confirmCurrencyOpened"
      title="Confirm currency change"
      label="Confirm"
      :submitLoading="submitLoading"
      @backdrop-close="onCancelChangeCurrency"
      @backdrop-action="onChangeCurrency(currencyInput)"
    >
      <div class="px-8" :style="{ width: '40rem' }">
        <div>
          <BlTextBody2 text="You are about to set the credit currency to" />
          <BlTextBody2
            :text="`${currencyName[currencyInput]}.`"
            class="font-bold"
          />
          <BlTextBody2
            text="Please note, this change can only be made once and cannot be reversed."
          />
        </div>
        <BlTextBody2 text="Do you want to proceed?" />
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="changeBranchOpened"
      title="Turn off branches setting"
      description="Do you want to turn off the Branches setting? All branch tags will be removed from the center and clients. Reactivating the setting will restore all previous branch data."
      label="Confirm"
      :submitLoading="submitLoading"
      @backdrop-close="changeBranchOpened = false"
      @backdrop-action="onChangeBranch"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { TIMEZONES } from "@/plugins/timezone.js";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputButton from "../BlInputButton.vue";
import BlIcon from "../BlIcon.vue";
import BlTooltip from "../BlTooltip.vue";
import BlInputText from "../BlInputText.vue";
import BlInputToggle from "../BlInputToggle.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "CenterGeneralInformation",
  components: {
    BlTextTitle4,
    BlTextBody2,
    BlInputButton,
    BlIcon,
    BlTooltip,
    BlInputText,
    BlInputToggle,
    BlSelectBox,
    BlBackdrop
  },
  data() {
    return {
      pageLoading: true,
      submitLoading: false,
      dropActive: false,
      logoUrlInput: null,
      fileUploadInput: null,
      nameInput: {
        placeholder: "Type the center name",
        model: ""
      },
      confirmCurrencyOpened: false,
      currencyInput: "",
      currencyOptions: [
        { value: "IDR", label: "IDR (Rp)" },
        { value: "USD", label: "USD ($)" },
        { value: "SGD", label: "SGD ($)" },
        { value: "MYR", label: "MYR (RM)" },
        { value: "PHP", label: "PHP (₱)" },
        { value: "THB", label: "THB (฿)" },
        { value: "VND", label: "VND (₫)" }
      ],
      currencyName: {
        IDR: "IDR (Rp)",
        USD: "USD ($)",
        SGD: "SGD ($)",
        MYR: "MYR (RM)",
        PHP: "PHP (₱)",
        THB: "THB (฿)",
        VND: "VND (₫)"
      },
      dateFormatInput: "",
      dateFormatOptions: [
        { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
        { value: "MM/DD/YYYY", label: "MM/DD/YYYY" }
      ],
      timezoneInput: "",
      timezoneOptions: TIMEZONES.map(t => ({
        value: t.timezone,
        label: t.name
      })),
      //
      changeBranchOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    isDisabledSubmit() {
      const logo = this.logoUrlInput;
      if (!logo || logo !== this.currentCenter.logo_url) return false;

      const name = this.nameInput.model;
      if (!name || name !== this.currentCenter.name) return false;

      const currency = this.currencyInput;
      if (!currency || currency !== this.currentCenter.currency) return false;

      // this.dateFormatInput !== this.currentCenter.date_format ||
      // this.timezoneInput !== this.currentCenter.timezone
      return true;
    }
  },
  watch: {
    currentCenter: {
      deep: true,
      immediate: true,
      handler(val) {
        this.generateInputs(val);
      }
    }
  },
  async mounted() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.addEventListener(eventName, this.preventDefaults);
    });

    this.pageLoading = true;
    if (!this.currentCenter.id) {
      await this.getCurrentCenter();
    } else {
      this.generateInputs(this.currentCenter);
    }
    this.pageLoading = false;
  },
  destroyed() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.removeEventListener(eventName, this.preventDefaults);
    });
  },
  methods: {
    ...mapActions(["getCurrentCenter", "updateCurrentCenter", "showToast"]),
    preventDefaults(e) {
      e.preventDefault();
    },
    generateInputs(val) {
      if (val && val.id) {
        this.logoUrlInput = val.logo_url;
        this.nameInput.model = val.name;
        this.currencyInput = val.currency;
        // this.dateFormatInput = val.date_format;
        // this.timezoneInput = val.timezone;
      }
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    openUpload() {
      const input = document.getElementById("logo_url_input");
      if (input) {
        input.click();
      }
    },
    onDropUpload(e) {
      this.dropActive = false;
      const files = e.dataTransfer.files;
      if (files && files.length) {
        this.onChangeLogo(files[0]);
      }
    },
    onButtonUpload(e) {
      const files = e.target.files;
      if (files && files.length) {
        this.onChangeLogo(files[0]);
      } else {
        this.onRemoveLogo();
      }
    },
    onChangeLogo(file) {
      const type = file.type;
      if (!type.includes("image")) {
        const title = "Unable to upload file";
        const message = "Failed! Please use an image file for center logo.";
        this.showToast({ title, message, type: "error" });
        return;
      }
      this.fileUploadInput = file;
      this.logoUrlInput = URL.createObjectURL(file);
    },
    onRemoveLogo() {
      this.fileUploadInput = null;
      this.logoUrlInput = null;
    },
    //
    async onUpdate() {
      const formData = new FormData();
      formData.append("center[logo]", this.fileUploadInput);
      formData.append("center[name]", this.nameInput.model);
      formData.append("center[currency]", this.currencyInput);
      // formData.append('center[date_format]', this.dateFormatInput);
      // formData.append('center[timezone]', this.timezoneInput);

      const jsonCenterData = {
        name: this.nameInput.model,
        currency: this.currencyInput
        // date_format: this.dateFormatInput,
        // timezone: this.timezoneInput
      };

      /*
        1. upload new logo: form data
            file upload exist
            logo url exist
        2. no update for logo: json data without logo
            file upload empty
            logo url exist
        3. remove logo: json data with { logo: null }
            file upload empty
            logo url empty
      */

      // prettier-ignore
      let params = {}
      if (this.fileUploadInput && this.logoUrlInput) {
        params = formData;
      }
      if (!this.fileUploadInput && this.logoUrlInput) {
        params = { center: jsonCenterData };
      }
      if (!this.fileUploadInput && !this.logoUrlInput) {
        params = { center: { logo: null, ...jsonCenterData } };
      }

      this.submitLoading = true;
      await this.updateCurrentCenter(params);
      this.submitLoading = false;
    },
    //
    async onChangeCurrency(val) {
      if (val) {
        this.currencyInput = val;
      }

      if (!this.confirmCurrencyOpened) {
        this.confirmCurrencyOpened = true;
        return;
      }
      const name = this.currencyName[this.currencyInput];
      const params = {
        center: {
          currency_confirmed: true,
          currency: this.currencyInput
        },
        title: "Currency updated",
        message: `Success! The currency has been updated to <b>${name}</b>.`
      };

      this.submitLoading = true;
      await this.updateCurrentCenter(params);
      this.submitLoading = false;
      this.confirmCurrencyOpened = false;
    },
    onCancelChangeCurrency() {
      this.confirmCurrencyOpened = false;
      this.generateInputs(this.currentCenter);
    },
    //
    async onChangeBranch() {
      const enabled = this.currentCenter.enable_branch === true;
      if (enabled && !this.changeBranchOpened) {
        this.changeBranchOpened = true;
        return;
      }
      const params = {
        center: { enable_branch: !enabled },
        message: "Success! The branches setting has been updated."
      };

      this.submitLoading = true;
      await this.updateCurrentCenter(params);
      this.submitLoading = false;
      this.changeBranchOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/GeneralInformation.vue